import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "../lib/api";
import { getAll, STORES } from "../lib/db";
import { formatCurrency } from "../lib/romEngine";
import { RepositoryInsightsContent } from "./Insights";
import {
    Briefcase, CurrencyDollar, FileText, ChartLineUp, Plus, ArrowRight, Crown, Brain,
    Kanban, Clock, WarningOctagon, TrendUp,
} from "@phosphor-icons/react";

export default function RomsOverview() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [offline, setOffline] = useState(false);
    const [portfolio, setPortfolio] = useState(null);
    const [alerts, setAlerts] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await endpoints.dashboard();
                setData(data);
                setOffline(false);
            } catch {
                // offline: compute from IndexedDB
                const [jobs, docs] = await Promise.all([
                    getAll(STORES.jobs), getAll(STORES.documents),
                ]);
                const active = jobs.filter((j) => ["draft","sent","approved","in_progress"].includes(j.status));
                const totalRom = jobs.reduce((s, j) => s + (j.result?.grand_total || 0), 0);
                const pending = docs.filter((d) => ["sent","viewed","draft"].includes(d.status));
                setData({
                    active_jobs: active.length,
                    total_jobs: jobs.length,
                    total_rom_value: totalRom,
                    pending_signatures: pending.length,
                    avg_variance_pct: null,
                    recent_jobs: [...jobs].sort((a,b)=>(b.updated_at||"").localeCompare(a.updated_at||"")).slice(0,8),
                });
                setOffline(true);
            }
        })();
        endpoints.portfolio().then((r) => setPortfolio(r.data)).catch(() => {});
        endpoints.changeRequests().then((r) => setAlerts(r.data || [])).catch(() => {});
    }, []);

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8 space-y-8">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">
                        Operations · Today
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
                        Estimator Control Room
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate("/calculator/new?scale=residential")}
                        className="btn btn-ghost"
                        data-testid="new-estimate-cta"
                    >
                        <Plus size={14} weight="bold" className="text-[var(--rom-green)]" /> New Estimate
                    </button>
                    <button
                        onClick={() => navigate("/calculator/new?scale=commercial")}
                        className="btn btn-primary"
                        data-testid="new-rom-cta"
                    >
                        <Plus size={14} weight="bold" /> New ROM
                    </button>
                </div>
            </header>

            {offline && (
                <div className="rom-card border-amber-300 bg-amber-50">
                    <div className="rom-card-body text-amber-900 text-sm">
                        Showing locally cached dashboard. Reconnect to sync the latest values.
                    </div>
                </div>
            )}

            {(() => {
                const changeAlerts = (alerts || []).filter((a) => a.status === "changes_requested");
                if (changeAlerts.length === 0) return null;
                return (
                    <div className="rounded-[6px] border-l-4 border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]/50 p-4" data-testid="dashboard-change-alerts">
                        <div className="flex items-center gap-2 mb-2">
                            <WarningOctagon size={18} weight="bold" className="text-[var(--rom-orange-dark)]" />
                            <h3 className="font-display font-bold text-sm">Changes requested ({changeAlerts.length})</h3>
                        </div>
                        <div className="space-y-2">
                            {changeAlerts.map((a) => {
                                const cr = a.payload?.change_request || {};
                                const items = cr.items || [];
                                return (
                                    <button key={a.id} onClick={() => a.job_id && navigate(`/calculator/${a.job_id}`)}
                                        className="w-full text-left bg-white border border-[var(--rom-border)] rounded-[4px] px-3 py-2 hover:border-[var(--rom-orange)] transition-colors"
                                        data-testid={`dashboard-change-${a.id}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-mono text-[11px] font-semibold text-[var(--rom-text-2)]">{a.payload?.ref || "ROM"}</span>
                                            <span className="text-[11px] text-[var(--rom-text-3)]">{cr.requested_by || a.payload?.recipient_name || "Recipient"}</span>
                                        </div>
                                        <div className="text-xs text-[var(--rom-text)] mt-1 flex flex-wrap gap-1.5 items-baseline">
                                            {items.slice(0, 4).map((it, i) => (
                                                <span key={i} className="chip chip-orange" style={{ padding: "0px 6px" }}>{it.tag}: {it.label}</span>
                                            ))}
                                            {items.length === 0 && cr.message && <span className="italic text-[var(--rom-text-2)]">“{cr.message}”</span>}
                                            {items.length > 4 && <span className="text-[var(--rom-text-3)]">+{items.length - 4} more</span>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {!offline && portfolio && portfolio.count > 0 && (
                <PortfolioRollup portfolio={portfolio} navigate={navigate} />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
                <Kpi
                    icon={Briefcase}
                    label="Active estimates"
                    value={data ? data.active_jobs : "—"}
                    sub={data ? `${data.total_jobs} total` : ""}
                    accent="navy"
                    testId="kpi-active-jobs"
                />
                <Kpi
                    icon={CurrencyDollar}
                    label="Total ROM value"
                    value={data ? formatCurrency(data.total_rom_value) : "—"}
                    sub="across all estimates"
                    accent="orange"
                    testId="kpi-rom-value"
                />
                <Kpi
                    icon={FileText}
                    label="Pending signatures"
                    value={data ? data.pending_signatures : "—"}
                    sub="docs awaiting sign-off"
                    accent="navy"
                    testId="kpi-pending-sigs"
                />
                <Kpi
                    icon={ChartLineUp}
                    label="ROM vs actual"
                    value={data && data.avg_variance_pct !== null && data.avg_variance_pct !== undefined
                        ? `${data.avg_variance_pct > 0 ? "+" : ""}${data.avg_variance_pct}%`
                        : "—"}
                    sub={data?.avg_variance_pct !== null && data?.avg_variance_pct !== undefined
                        ? "avg variance"
                        : "no actuals yet"}
                    accent="green"
                    testId="kpi-variance"
                />
            </div>

            <EstimatorEdgeCard score={data?.calibration_score} onOpen={() => navigate("/calibration")} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rom-card">
                    <div className="rom-card-head">
                        <h3><span className="rom-section-tag">E</span> Recent estimates</h3>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate("/estimates")}>
                            View all <ArrowRight size={12} weight="bold" />
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="rom-table">
                            <thead>
                                <tr>
                                    <th>Ref</th>
                                    <th>Loss</th>
                                    <th>Status</th>
                                    <th className="numeric">ROM</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.recent_jobs || []).length === 0 && (
                                    <tr><td colSpan={5} className="text-center text-[var(--rom-text-3)] py-8">
                                        No estimates yet. Start with a New ROM.
                                    </td></tr>
                                )}
                                {(data?.recent_jobs || []).map((j) => (
                                    <tr key={j.id} className="cursor-pointer" onClick={() => navigate(`/calculator/${j.id}`)}>
                                        <td className="font-mono text-xs">{j.ref}</td>
                                        <td className="capitalize">{j.loss_type}</td>
                                        <td><span className={`chip ${chipFor(j.status)}`}>{j.status?.replace("_"," ")}</span></td>
                                        <td className="numeric font-semibold">{formatCurrency(j.result?.grand_total)}</td>
                                        <td className="text-xs text-[var(--rom-text-2)]">{fmtDate(j.updated_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="rom-card">
                    <div className="rom-card-head"><h3><span className="rom-section-tag">Q</span> Quick start</h3></div>
                    <div className="rom-card-body space-y-3">
                        <QuickAction title="New Estimate" desc="Residential — quick room-by-room quote" to="/calculator/new?scale=residential" />
                        <QuickAction title="New ROM" desc="Commercial / large-loss sq-ft calculator" to="/calculator/new?scale=commercial" />
                        <QuickAction title="Add a client" desc="Customers, insurers, claim refs" to="/clients" />
                        <QuickAction title="Review rate schedule" desc="2026 T&M Schedule A/B/C" to="/rate-schedule" />
                        <QuickAction title="Documents board" desc="Draft → Sent → Signed" to="/documents" />
                    </div>
                </div>
            </div>

            {!offline && (
                <div className="pt-2 border-t border-[var(--rom-border)]" data-testid="dashboard-insights-section">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-4 mt-4">
                        Repository · Insights
                    </div>
                    <RepositoryInsightsContent />
                </div>
            )}
        </div>
    );
}

function EstimatorEdgeCard({ score, onOpen }) {
    const withBoth = score?.jobs_with_engine_prediction || 0;
    const pct = score?.beat_engine_pct;
    return (
        <div className="rom-card border-l-4 border-l-[var(--rom-orange)]" data-testid="estimator-edge-card">
            <div className="rom-card-body flex flex-col md:flex-row md:items-center gap-5">
                <div className="flex items-center gap-3 shrink-0">
                    <div className="w-11 h-11 rounded-[6px] bg-[var(--rom-orange-soft)] text-[var(--rom-orange-dark)] flex items-center justify-center">
                        <Crown size={22} weight="fill" />
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-[var(--rom-text-3)] font-semibold">Estimator&apos;s edge</div>
                        <div className="font-display font-black text-lg leading-tight">Beating the raw engine</div>
                    </div>
                </div>

                {withBoth > 0 ? (
                    <>
                        <div className="flex items-baseline gap-2">
                            <div className="font-display font-black text-4xl tracking-tight text-[var(--rom-orange-dark)]" data-testid="edge-pct">
                                {pct == null ? "—" : `${pct}%`}
                            </div>
                            <div className="text-sm text-[var(--rom-text-2)]">
                                of estimates where edits beat the engine
                                <div className="text-[11px]">{score.estimator_improved_count} of {withBoth} closed estimates w/ an engine baseline</div>
                            </div>
                        </div>
                        <div className="flex gap-6 md:ml-auto text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">Estimator MAE</div>
                                <div className="font-mono font-bold">{score.estimator_mae_pct != null ? `±${score.estimator_mae_pct}%` : "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">Raw engine MAE</div>
                                <div className="font-mono font-bold">{score.engine_mae_pct != null ? `±${score.engine_mae_pct}%` : "—"}</div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-[var(--rom-text-2)] flex-1">
                        Save ROMs and record their actual invoices to start scoring how often your estimators&apos; judgement beats the raw coefficient engine. Every correction makes the model smarter.
                    </div>
                )}

                <button className="btn btn-ghost btn-sm shrink-0 md:ml-2" onClick={onOpen} data-testid="edge-open-tuner">
                    <Brain size={13} weight="bold" /> Coefficient Tuner <ArrowRight size={12} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function PortfolioRollup({ portfolio, navigate }) {
    const p = portfolio;
    const marginColor = p.blended_margin_pct == null ? "var(--rom-navy)" : p.blended_margin_pct < 0 ? "#dc2626" : p.blended_margin_pct < 15 ? "#d97706" : "var(--rom-green)";
    return (
        <div className="rom-card" data-testid="portfolio-rollup" style={{ borderLeft: "3px solid var(--rom-orange)" }}>
            <div className="rom-card-body space-y-5">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold flex items-center gap-2">
                        <Kanban size={14} weight="bold" className="text-[var(--rom-orange)]" /> Portfolio · Live workflow health
                    </div>
                    <button onClick={() => navigate("/projects")} className="text-xs text-[var(--rom-orange-dark)] font-semibold hover:underline flex items-center gap-1">
                        Open pipeline <ArrowRight size={12} weight="bold" />
                    </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <RollStat icon={CurrencyDollar} label="Pipeline value" value={formatCurrency(p.total_pipeline_value)} sub={`${p.active_count} active · ${p.count} total`} color="var(--rom-navy)" />
                    <RollStat icon={TrendUp} label="Blended margin" value={p.blended_margin_pct != null ? `${p.blended_margin_pct}%` : "—"} sub={formatCurrency(p.total_margin)} color={marginColor} />
                    <RollStat icon={Clock} label="Labour this week" value={formatCurrency(p.week_labour_cost)} sub={`${p.week_labour_hours}h logged`} color="var(--rom-orange)" />
                    <RollStat icon={CurrencyDollar} label="Outstanding AR" value={formatCurrency(p.outstanding_ar)} sub="invoiced − paid" color={p.outstanding_ar > 0 ? "#d97706" : "var(--rom-green)"} />
                </div>
                {p.at_risk.length > 0 && (
                    <div className="pt-4 border-t border-[var(--rom-border)]" data-testid="at-risk-list">
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#dc2626] mb-2">
                            <WarningOctagon size={16} weight="bold" /> At-risk projects ({p.at_risk.length})
                            <span className="text-[var(--rom-text-3)] font-normal text-xs">· burn &gt; 90% or negative margin</span>
                        </div>
                        <div className="space-y-1.5">
                            {p.at_risk.slice(0, 5).map((r) => (
                                <button key={r.id} onClick={() => navigate(`/projects/${r.id}`)} data-testid={`at-risk-${r.id}`}
                                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-[4px] bg-red-50/60 hover:bg-red-50 border border-red-100 transition-colors text-left">
                                    <span className="font-medium text-sm truncate">{r.name}</span>
                                    <span className="flex items-center gap-3 shrink-0 text-xs">
                                        <span className="text-[var(--rom-text-2)]">burn {r.burn_pct ?? "—"}%</span>
                                        <span className="font-semibold font-mono" style={{ color: r.margin_pct < 0 ? "#dc2626" : "#d97706" }}>{r.margin_pct ?? "—"}%</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function RollStat({ icon: Icon, label, value, sub, color }) {
    return (
        <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[var(--rom-text-2)] font-semibold">
                <Icon size={13} weight="bold" style={{ color }} /> {label}
            </div>
            <div className="mt-1.5 font-display font-black text-2xl tracking-tight" style={{ color }}>{value}</div>
            {sub && <div className="text-[11px] text-[var(--rom-text-3)]">{sub}</div>}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, accent, testId }) {
    const ringColor = {
        orange: "var(--rom-orange)",
        navy: "var(--rom-navy)",
        green: "var(--rom-green)",
    }[accent] || "var(--rom-navy)";
    return (
        <div className="rom-card" data-testid={testId}>
            <div className="rom-card-body">
                <div className="flex items-start justify-between">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">
                        {label}
                    </div>
                    <div className="w-8 h-8 rounded-[4px] flex items-center justify-center"
                         style={{ background: `${ringColor}14`, color: ringColor }}>
                        <Icon size={16} weight="bold" />
                    </div>
                </div>
                <div className="mt-3 font-display font-black text-3xl tracking-tight" style={{ color: ringColor }}>
                    {value}
                </div>
                {sub && <div className="text-xs text-[var(--rom-text-2)] mt-1">{sub}</div>}
            </div>
        </div>
    );
}

function QuickAction({ title, desc, to }) {
    const navigate = useNavigate();
    return (
        <button
            onClick={() => navigate(to)}
            className="w-full text-left flex items-center justify-between p-3 border border-[var(--rom-border)] rounded-[4px] hover:border-[var(--rom-navy)] transition-colors"
            data-testid={`quick-${to.replace("/", "")}`}
        >
            <div>
                <div className="font-semibold text-sm">{title}</div>
                <div className="text-xs text-[var(--rom-text-2)]">{desc}</div>
            </div>
            <ArrowRight size={14} className="text-[var(--rom-text-2)]" weight="bold" />
        </button>
    );
}

function chipFor(status) {
    return {
        draft: "chip-gray",
        sent: "chip-orange",
        approved: "chip-green",
        in_progress: "chip-navy",
        closed: "chip-gray",
    }[status] || "chip-gray";
}

function fmtDate(s) {
    if (!s) return "";
    try { return new Date(s).toLocaleDateString(); } catch { return ""; }
}
